import React from 'react';
import { motion } from 'framer-motion';
import { Heart } from 'lucide-react';
import { Player, Club } from '../../types/api.types';
import AnimatedText from '../AnimatedText';
import TTStatsVerified from '../TTStatsVerified';
import { getVerificationStatus } from '../../config/verifiedUsers';
import { getClassificationColor, getClassificationForElo } from '../../utils/classificationUtils';

interface SearchResultsProps {
  players: Player[];
  clubs: Club[];
  currentLang: 'en' | 'fr' | 'de' | 'it';
  isFavorite: (url: string) => boolean;
  onPlayerSelect: (player: Player) => void;
  onClubSelect: (clubId: string) => void;
  t: {
    players: string;
    clubs: string;
  };
}

/**
 * SearchResults Component
 * Displays search results for players and clubs
 */
const SearchResults: React.FC<SearchResultsProps> = ({
  players,
  clubs,
  currentLang,
  isFavorite,
  onPlayerSelect,
  onClubSelect,
  t
}) => {
  // Format player name
  const formatPlayerName = (name: string) => {
    if (name.includes(',')) {
      const [lastName, firstName] = name.split(',').map(s => s.trim());
      return `${firstName} ${lastName}`;
    }
    return name;
  };

  return (
    <motion.div
      className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-300 dark:border-gray-700 max-h-96 overflow-y-auto z-50 transition-colors duration-300"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
    >
      {/* Players Section */}
      {players.length > 0 && (
        <>
          <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700 border-b border-gray-100 dark:border-gray-600 transition-colors duration-300">
            <AnimatedText
              text={t.players}
              className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider text-left transition-colors duration-300"
              delay={0.1}
              staggerDelay={0.02}
            />
          </div>
          {players.map((player, index) => (
            <motion.div
              key={index}
              className="group"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.3,
                delay: 0.1 + index * 0.05,
                ease: [0.25, 0.1, 0.25, 1.0]
              }}
            >
              <motion.div
                onClick={() => onPlayerSelect(player)}
                className="px-6 py-4 cursor-pointer transition-all border-b border-gray-50 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                whileHover={{
                  transition: { duration: 0.2 }
                }}
              >
                <div className="flex items-start justify-between">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.06 + 0.1 }}
                    className="flex-1 min-w-0 mr-3 text-left"
                  >
                    <motion.div
                      className="flex items-center gap-2"
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.06 + 0.15 }}
                    >
                      <p className="font-medium text-gray-900 dark:text-gray-100 truncate text-left transition-colors duration-300">
                        {formatPlayerName(player.name)}
                      </p>
                      {(() => {
                        const verificationType = getVerificationStatus(player.name, player.url);
                        return verificationType && (
                          <TTStatsVerified
                            size="sm"
                            animate={false}
                            lang={currentLang}
                            type={verificationType}
                          />
                        );
                      })()}
                    </motion.div>
                    {player.club && (
                      <motion.p
                        className="text-sm text-gray-500 dark:text-gray-400 truncate mt-1 text-left transition-colors duration-300"
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.06 + 0.2 }}
                      >
                        {player.club}
                      </motion.p>
                    )}
                  </motion.div>
                  <motion.div
                    className="flex items-center gap-2 flex-shrink-0"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{
                      delay: index * 0.06 + 0.25,
                      type: "spring",
                      stiffness: 200,
                      damping: 15
                    }}
                  >
                    {/* Favorite indicator */}
                    {player.url && isFavorite(player.url) && (
                      <Heart className="w-4 h-4 fill-red-500 text-red-500" />
                    )}
                    {(() => {
                      const classification = player.classification || (player.elo ? getClassificationForElo(player.elo) : null);
                      if (!classification) return null;
                      const colors = getClassificationColor(classification);
                      return (
                        <motion.span
                          className="text-xs font-bold px-2.5 py-1 rounded-lg"
                          style={{
                            backgroundColor: colors.bg,
                            color: colors.text
                          }}
                          whileHover={{
                            scale: 1.05,
                            transition: { duration: 0.2 }
                          }}
                        >
                          {classification}
                        </motion.span>
                      );
                    })()}
                    {player.elo && (
                      <motion.span
                        className="text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-lg whitespace-nowrap transition-colors duration-300"
                        whileHover={{
                          scale: 1.05,
                          transition: { duration: 0.2 }
                        }}
                      >
                        {player.elo}
                      </motion.span>
                    )}
                  </motion.div>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </>
      )}

      {/* Clubs Section */}
      {clubs.length > 0 && (
        <>
          <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700 border-b border-gray-100 dark:border-gray-600 transition-colors duration-300">
            <AnimatedText
              text={t.clubs}
              className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider text-left transition-colors duration-300"
              delay={0.1}
              staggerDelay={0.02}
            />
          </div>
          {clubs.map((club, index) => (
            <motion.div
              key={index}
              className="group"
              initial={{ opacity: 0, x: -20 }}
              animate={{
                opacity: 1,
                x: 0
              }}
              transition={{
                duration: 0.3,
                delay: 0.1 + (players.length + index) * 0.05,
                ease: [0.25, 0.1, 0.25, 1.0]
              }}
            >
              <motion.div
                onClick={() => {
                  const clubId = club.id || (club.href ? club.href.replace(/.*club=/, '') : '');
                  if (clubId) {
                    onClubSelect(clubId);
                  }
                }}
                className="px-6 py-4 cursor-pointer transition-all border-b border-gray-50 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-700"
                whileHover={{
                  transition: { duration: 0.2 }
                }}
              >
                <div className="flex items-center justify-between">
                  <motion.div
                    className="flex-1 min-w-0 mr-3"
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: (players.length + index) * 0.06 + 0.1 }}
                  >
                    <p className="font-medium text-gray-900 dark:text-gray-100 truncate text-left transition-colors duration-300">
                      {club.name}
                    </p>
                  </motion.div>
                  <motion.span
                    className="text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-lg whitespace-nowrap transition-colors duration-300"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{
                      delay: (players.length + index) * 0.06 + 0.25,
                      type: "spring",
                      stiffness: 200,
                      damping: 15
                    }}
                    whileHover={{
                      scale: 1.05,
                      transition: { duration: 0.2 }
                    }}
                  >
                    Club
                  </motion.span>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </>
      )}
    </motion.div>
  );
};

export default SearchResults;
